import { isApplePlatform } from '@/utils/browser/platform';
import { capitalize } from 'lodash-es';

export enum EShortcutScope {
  GLOBAL,
  EDITOR,
  DRAWING,
  MODAL,
  OVERLAY
}

export enum EShortcutId {
  // 编辑操作
  UNDO = 'undo',
  REDO = 'redo',
  ESCAPE = 'escape',

  // 方案操作
  SCHEME_SAVE = 'scheme-save',
  SCHEME_SAVE_AS = 'scheme-save-as',

  // 视图操作
  FIT_TO_VIEW = 'fit-to-view',
  VIEW_2D = '2d-view',
  VIEW_3D = '3d-view',

  // 实体操作
  REPLACE_ENTITY = 'replace-entity',
  FLIP_ENTITY = 'flip-entity',
  REMOVE_ENTITY = 'delete-entity',
  COPY_ENTITY = 'copy-entity',
  MOVE_ENTITY_DOWN = 'move-entity-down',
  ASSEMBLE = 'assemble',
  UNASSEMBLE = 'unassemble',
  MOVE_ENTITY_UP = 'move-entity-up',
  MOVE_ENTITY_LEFT = 'move-entity-left',
  MOVE_ENTITY_RIGHT = 'move-entity-right',

  // 绘制工具
  DRAW_WALL = 'draw-wall',
  DRAW_RECTANGLE_WALL = 'draw-rectangle-wall',
  DRAW_ARC_WALL = 'draw-arc-wall',

  // DXF 底图图层
  FLOOR_PLAN_LAYER_SELECT_ALL = 'floor-plan-layer-select-all',

  // 全局操作
  TAB = 'tab'
}

export enum EShortcutPlatform {
  MAC = 'mac',
  WINDOWS = 'windows'
}

/**
 * 获取当前平台
 */
export function getCurrentPlatform(): EShortcutPlatform {
  if (isApplePlatform()) {
    return EShortcutPlatform.MAC;
  }
  return EShortcutPlatform.WINDOWS;
}

interface ShortcutConfig {
  /** 快捷键组合（支持多个） */
  keys: string[];
  /** 是否允许事件继续传播 */
  noCapture?: boolean;
  /** 作用域 */
  scope: EShortcutScope[];
  /** 平台特定的键位映射 */
  platformKeys?: Partial<Record<EShortcutPlatform, string[]>>;
  /** 注册后是否阻止默认行为（快捷键禁用/启用都会） */
  preventDefault?: boolean;
}

/**
 * 快捷键配置表
 */
export const SHORTCUT_MAP: Record<EShortcutId, ShortcutConfig> = {
  [EShortcutId.UNDO]: {
    keys: ['mod+z'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR]
  },
  [EShortcutId.REDO]: {
    keys: ['mod+shift+z', 'mod+y'],
    noCapture: true,
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR]
  },
  [EShortcutId.SCHEME_SAVE]: {
    keys: ['mod+s'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR],
    preventDefault: true
  },
  [EShortcutId.SCHEME_SAVE_AS]: {
    keys: ['mod+shift+s'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR]
  },
  [EShortcutId.FIT_TO_VIEW]: {
    keys: [' '],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR]
  },
  [EShortcutId.DRAW_WALL]: {
    keys: ['l'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR]
  },
  [EShortcutId.DRAW_RECTANGLE_WALL]: {
    keys: ['r'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR]
  },
  [EShortcutId.DRAW_ARC_WALL]: {
    keys: ['u'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR]
  },
  [EShortcutId.VIEW_2D]: {
    keys: ['1'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR]
  },
  [EShortcutId.VIEW_3D]: {
    keys: ['3'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR]
  },
  [EShortcutId.REMOVE_ENTITY]: {
    keys: ['delete'], // 默认键位
    scope: [EShortcutScope.EDITOR],
    platformKeys: {
      [EShortcutPlatform.MAC]: ['backspace'],
      [EShortcutPlatform.WINDOWS]: ['delete', 'backspace']
    }
  },
  [EShortcutId.COPY_ENTITY]: {
    keys: ['mod+c'],
    scope: [EShortcutScope.EDITOR]
  },
  [EShortcutId.ASSEMBLE]: {
    keys: ['g'],
    scope: [EShortcutScope.EDITOR]
  },
  [EShortcutId.UNASSEMBLE]: {
    keys: ['x'],
    scope: [EShortcutScope.EDITOR]
  },
  [EShortcutId.ESCAPE]: {
    keys: ['escape'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR, EShortcutScope.DRAWING]
  },
  [EShortcutId.REPLACE_ENTITY]: {
    keys: ['p'],
    scope: [EShortcutScope.EDITOR]
  },
  [EShortcutId.FLIP_ENTITY]: {
    keys: ['f'],
    scope: [EShortcutScope.EDITOR]
  },

  [EShortcutId.MOVE_ENTITY_DOWN]: {
    keys: ['arrowdown'],
    scope: [EShortcutScope.EDITOR]
  },
  [EShortcutId.MOVE_ENTITY_UP]: {
    keys: ['arrowup'],
    scope: [EShortcutScope.EDITOR]
  },
  [EShortcutId.MOVE_ENTITY_LEFT]: {
    keys: ['arrowleft'],
    scope: [EShortcutScope.EDITOR]
  },
  [EShortcutId.MOVE_ENTITY_RIGHT]: {
    keys: ['arrowright'],
    scope: [EShortcutScope.EDITOR]
  },
  [EShortcutId.FLOOR_PLAN_LAYER_SELECT_ALL]: {
    keys: ['mod+a'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR]
  },
  [EShortcutId.TAB]: {
    keys: ['tab'],
    scope: [EShortcutScope.GLOBAL, EShortcutScope.EDITOR, EShortcutScope.DRAWING]
  }
} as const;

/**
 * 根据当前平台获取快捷键组合
 */
export function getShortcutKeys(shortcut: EShortcutId, platform?: EShortcutPlatform): string[] {
  const config = SHORTCUT_MAP[shortcut];
  const currentPlatform = platform ?? getCurrentPlatform();

  // 如果有平台特定配置，使用平台特定的键位
  if (config.platformKeys && config.platformKeys[currentPlatform]) {
    return config.platformKeys[currentPlatform];
  }

  // 否则使用默认键位
  return config.keys;
}

/**
 * 将快捷键组合转换为显示文本
 */
export function formatShortcutKeys(keys: string[], platform?: EShortcutPlatform): string {
  const currentPlatform = platform ?? getCurrentPlatform();
  const isMac = currentPlatform === EShortcutPlatform.MAC;

  // 使用第一个键位作为显示
  const keyCombo = keys[0] || '';
  const parts = keyCombo.toLowerCase().split('+');

  const formatted = parts.map((part) => {
    switch (part) {
      case 'mod':
        return isMac ? '⌘' : 'Ctrl';
      case 'ctrl':
        return isMac ? '⌃' : 'Ctrl';
      case 'alt':
        return isMac ? '⌥' : 'Alt';
      case 'shift':
        return isMac ? '⇧' : 'Shift';
      case 'meta':
        return isMac ? '⌘' : 'Win';
      case 'backspace':
        return isMac ? '⌫' : 'Backspace';
      case 'delete':
        return isMac ? '⌦' : 'Delete';
      case 'escape':
        return isMac ? '⎋' : 'Esc';
      case 'enter':
        return isMac ? '↩' : 'Enter';
      case ' ':
        return isMac ? '␣' : 'Space';
      case 'arrowup':
        return '↑';
      case 'arrowdown':
        return '↓';
      case 'arrowleft':
        return '←';
      case 'arrowright':
        return '→';
      default:
        return capitalize(part);
    }
  });

  return formatted.join(isMac ? '' : '+');
}
